import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { first, Subscription } from 'rxjs';
import { Editor } from 'primeng/editor';

import { ApolloMutationService } from 'app/shared/apollo/index';
import { FormHandlerService } from 'app/shared/forms';
import { ExtendedFormGroup } from 'app/shared/forms/extended-form-group';
import { GlobalService } from 'app/shared/global/index';
import { UpdateCompanySettingsGQL } from '../company-update-settings/graphql/company-update-settings.generated';
import { GetCompanySettingsGQL } from '../company-update-settings/graphql/company-update-settings.generated';

import { AccountingPlanService } from '../services/accounting-plan.service';
import {
  AccountingPlanAccount,
  AccountingPlanAccountsDropdown,
  AccountingPlanAccountsDropdownItem,
} from '../services/accounting-plan.types';
import { UserFlagsService } from 'app/user-flags.service';
import { HyperionErrorHandler } from 'app/shared/errors';
import { MessageService } from 'app/shared/message';
import { ActivateElectronicInvoiceGQL } from './graphql/activate-electronic-invoice.mutation.generated';
import { CompanyFunctionsService } from 'app/shared/company';
@Component({
  selector: 'companyInvoiceOffer-settings',
  templateUrl: 'company-invoice-offer-settings.component.html',
  providers: [FormHandlerService],
})
export class CompanyInvoiceOfferSettingsComponent implements OnInit, OnDestroy {
  public componentMainForm: ExtendedFormGroup;
  private dataModel = 'company';
  private dataModelCapitalized: string;
  private formFields = {
    model: 'Company',
    nestedForm: false,
    attributes: {
      id: '',
      betalningsvillkor: '',
      interest: '',
      invoiceReminderFee: '',
      plusGiro: '',
      postGiro: '',
      iban: '',
      bicSwift: '',
      companyInsurance: '',
      fTax: '',
      showPdfInvoicePricesWithVatPrivate: '',
      skra: '',
      standardInvoiceMailText: '',
      standardOfferMailText: '',
      socialInsurance: '',
      trakHalfDayPrice: '',
      trakDayPrice: '',
      trakNightPrice: '',
      weekendHourProcent: '',
      trakHalfDayAccountingPlanAccountId: '',
      reverseVatTrakHalfDayAccountingPlanAccountId: '',
      trakNightAccountingPlanAccountId: '',
      reverseVatTrakNightAccountingPlanAccountId: '',
      trakDayAccountingPlanAccountId: '',
      reverseVatTrakDayAccountingPlanAccountId: '',
    },
  };
  public fieldsForForm = {};
  public setFields = {};
  public skraOptions = [
    { label: 'Bygg', value: 'Bygg' },
    { label: 'El', value: 'El' },
    { label: 'Glas & Plåtarbete', value: 'GlasPlatarbete' },
    { label: 'Mark & Dräneringsarbete', value: 'MarkDraneringarbete' },
    { label: 'Murning', value: 'Murning' },
    { label: 'Målning & Tapetsering', value: 'MalningTapetsering' },
    { label: 'Vvs', value: 'Vvs' },
  ];
  public buttons: any;
  public componentLoading = true;
  public betalningsvillkorOptions = [
    { label: '0 dagar', value: 0 },
    { label: '10 dagar', value: 10 },
    { label: '15 dagar', value: 15 },
    { label: '20 dagar', value: 20 },
    { label: '30 dagar', value: 30 },
    { label: '45 dagar', value: 45 },
    { label: '60 dagar', value: 60 },
    { label: '90 dagar', value: 90 },
  ];
  public acceptLink = '{acceptLink}';
  public invoiceMailPlaceholders: { label: string; value: string }[] = [];
  public selectedInvoiceMailPlaceholder: string = null;
  private defaultInvoiceMailText = '';
  @ViewChild('invoiceMailEditor') private invoiceMailEditor: Editor;
  private sub: Subscription;

  public companyAccountingPlanAccounts: AccountingPlanAccount[];
  public accountingPlanAccountsDropdownParams: AccountingPlanAccountsDropdown;
  public accountingPlanAccountsDropdownItemMap: {
    [key: number]: AccountingPlanAccountsDropdownItem;
  } = {};
  public hasAccountingPlanFunction: boolean;
  public isElectronicInvoiceEnabled = false;

  constructor(
    private mutationService: ApolloMutationService,
    private formHandler: FormHandlerService,
    private globalService: GlobalService,
    private getCompanySettingsService: GetCompanySettingsGQL,
    private updateCompanySettingsService: UpdateCompanySettingsGQL,
    private accountingPlanService: AccountingPlanService,
    private userFlagsService: UserFlagsService,
    private errorHandler: HyperionErrorHandler,
    private messageService: MessageService,
    private activateElectronicInvoiceGQL: ActivateElectronicInvoiceGQL,
    private companyFunctionsService: CompanyFunctionsService
  ) {
    const buttonObject = {
      update: {
        model: this.dataModel,
      },
    };

    this.dataModelCapitalized = this.globalService.capitalizeFirstLetter(
      this.dataModel
    );
    this.buttons = this.formHandler.getButtonValues(buttonObject);
  }

  public ngOnInit(): void {
    this.queryData();
    this.getAccountingPlans();
    this.userFlagsService
      .getFlags()
      .pipe(first())
      .subscribe(flags => {
        this.hasAccountingPlanFunction = flags.hasFlag('useAccountingPlan');
      });

    this.companyFunctionsService
      .hasCompanyFunction('useElectronicInvoice')
      .pipe(first())
      .subscribe(hasFunction => {
        this.isElectronicInvoiceEnabled = hasFunction;
      });
  }

  private queryData(): void {
    this.getCompanySettingsService
      .fetch()
      .pipe(first())
      .subscribe(({ data }) => {
        this.defaultInvoiceMailText = data.company.defaultInvoiceMailText ?? '';
        this.invoiceMailPlaceholders = (
          data.company.invoiceMailPlaceholders ?? []
        ).map(placeholder => ({
          label: placeholder.label,
          value: placeholder.placeholder,
        }));
        this.initForm(data.company);
      });
  }

  private initForm(dataToset: any): void {
    this.formHandler
      .groupSetLabelsRules(this.formFields, null, dataToset)
      .then(groupedForm => {
        this.componentMainForm = groupedForm;
        this.componentLoading = false;

        this.componentMainForm.controls.fTax.setValue(!!dataToset.fTax);
        this.componentMainForm.controls.showPdfInvoicePricesWithVatPrivate.setValue(
          !!dataToset.showPdfInvoicePricesWithVatPrivate
        );
        if (!dataToset.standardInvoiceMailText) {
          this.componentMainForm.controls.standardInvoiceMailText.setValue(
            this.defaultInvoiceMailText
          );
        }
      });
  }

  public ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  public updateRequest(event: KeyboardEvent): void {
    const target = event.target;
    if (!target['classList'].contains('ql-editor')) {
      this.actionUpdate();
    }
  }

  public actionUpdate(): void {
    if (this.formHandler.formValid([this.componentMainForm])) {
      const dataToMutation = this.componentMainForm.value;
      dataToMutation['id'] = Number(dataToMutation['id']);

      this.buttons = this.formHandler.lockButtons(this.buttons);

      this.updateCompanySettingsService
        .mutate({ companySettings: dataToMutation })
        .pipe(first())
        .subscribe({
          next: res => {
            this.buttons = this.formHandler.unlockButtons(this.buttons);

            this.mutationService.displayMutationStatus(
              res.data.companyTypeHyperionMutation
            );
          },
          error: error => {
            this.buttons = this.formHandler.unlockButtons(this.buttons);

            this.errorHandler.handleError(error);
          },
        });
    } else {
      const errors = this.formHandler.getMergedErrors([this.componentMainForm]);
      this.messageService.insertData({
        textArray: errors.map(error => `${error.label}: ${error.error}`),
        time: 0,
        type: 'error',
      });
    }
  }

  public insertInvoiceMailPlaceholder(placeholder: string): void {
    // Reset after the dropdown has written its own value, so the placeholder text shows again
    setTimeout(() => (this.selectedInvoiceMailPlaceholder = null));
    if (!placeholder) {
      return;
    }

    const quill = this.invoiceMailEditor?.getQuill();
    if (!quill) {
      const control = this.componentMainForm.controls.standardInvoiceMailText;
      control.setValue((control.value ?? '') + placeholder);
      return;
    }

    const range = quill.getSelection(true);
    const index = range ? range.index : Math.max(quill.getLength() - 1, 0);
    quill.insertText(index, placeholder, 'user');
    quill.setSelection(index + placeholder.length, 0);
  }

  public resetInvoiceMailText(): void {
    this.componentMainForm.controls.standardInvoiceMailText.setValue(
      this.defaultInvoiceMailText
    );
  }

  public insertAcceptLink(): void {
    if (
      !this.componentMainForm.controls.standardOfferMailText.value.includes(
        this.acceptLink
      )
    ) {
      this.componentMainForm.controls.standardOfferMailText.setValue(
        this.componentMainForm.controls.standardOfferMailText.value +
          this.acceptLink
      );
    }
  }

  public cancelEdit(event: KeyboardEvent): void {
    event.preventDefault();
  }

  private getAccountingPlans(): void {
    this.accountingPlanService
      .getAccounts()
      .subscribe((accounts: AccountingPlanAccount[]) => {
        this.companyAccountingPlanAccounts = accounts;

        this.accountingPlanAccountsDropdownParams =
          this.accountingPlanService.getAccountsDropdownParams(
            this.companyAccountingPlanAccounts
          );
        this.accountingPlanAccountsDropdownParams.options.forEach(
          i => (i.value = String(i.value))
        );
        this.accountingPlanAccountsDropdownParams.options.forEach(
          i => (this.accountingPlanAccountsDropdownItemMap[i.value] = i)
        );
      });
  }

  public toggleElectronicInvoice(): void {
    this.activateElectronicInvoiceGQL
      .mutate({
        action: this.isElectronicInvoiceEnabled ? 'deactivate' : 'activate',
      })
      .pipe(first())
      .subscribe({
        next: res => {
          this.mutationService.displayMutationStatus(
            res.data?.crediflowActivateParty
          );
          this.queryData();
          const success =
            res.data?.crediflowActivateParty?.mutationDetails?.[0]
              ?.mutationSucceeded ?? false;

          if (success) {
            this.isElectronicInvoiceEnabled = !this.isElectronicInvoiceEnabled;
            this.companyFunctionsService.refreshCompanyFunctions();
          }
        },
        error: error => {
          this.errorHandler.handleError(error);
        },
      });
  }
}
